<!--
/**
 * Author: David Schiefer
 * Department: ITS Development Tax

 * The Crud Table Component can be very convenient to display a simple table
 * the most important thing to understand is that it takes a store with the definition if TBaseStore
 * this allowed the crud table to work based on the connected store definition and give you basic crud operations
 * per default. There are many ways to configure the crud table according to your needs
 *
 */
!-->
<template>
    <div>
        <div v-if="create" class="d-flex align-center justify-space-between pa-2">
            <v-btn
                id="template-activator"
                :text="createBtnTitle_"
                color="primary"
                prepend-icon="mdi-plus"
                variant="elevated"
            />
        </div>
        <v-data-table-server
            v-if="serverSideTable"
            :density="density"
            :headers="(tableHeaders as any)"
            :items="filteredItems"
            :items-length="totalItems"
            :search="filterTemplate"
            :loading="isLoading"
            @update:options="loadItems"
            class="elevation-0 mt-0"
        >
            <template v-slot:top>
                <v-toolbar :flat="true" color="white">
                    <slot name="topLeft"></slot>
                    <v-text-field
                        v-if="searchFilter"
                        v-model="filterTemplate"
                        class="ms-4"
                        prepend-icon="mdi-magnify"
                        variant="underlined"
                        :placeholder="$t('generic.crud.search')"
                    />
                    <v-spacer/>
                    <div/>
                    <v-spacer/>
                    <dynamic-dialog
                        v-model="dialogDelete"
                        :cancel-text="deleteAbortText_"
                        :close-on-confirm="closeCancelDialogOnConfirm"
                        :confirm-text="deleteConfirmText_"
                        :title="deleteTitle_"
                        close-on-cancel
                        confirm-type="delete"
                        max-width="500px"
                        progress-liner-on-confirm
                        @confirm="deleteExecute"
                        :suppress-cancel-message="suppressDeleteCancelMessage"
                        :suppress-confirm-message="suppressDeleteConfirmMessage"
                    >
                        <template #formItems>
                            <slot name="deleteForm"
                                  v-bind:focusItem="focusItem"
                                  v-bind:outprops="outprops"
                            ></slot>
                        </template>
                    </dynamic-dialog>
                    <dynamic-dialog
                        v-model="dialogEdit"
                        :cancel-text="editAbortText_"
                        :close-on-confirm="closeEditDialogOnConfirm"
                        :confirm-disabled="!canSave"
                        :confirm-text="editConfirmText_"
                        :on-confirming="editExecute"
                        :title="editTitle_"
                        activator="#template-activator"
                        close-on-cancel
                        confirm-type="custom"
                        hide-action-btns
                        max-width="1000px"
                        :suppress-cancel-message="suppressEditCancelMessage"
                        :suppress-confirm-message="suppressEditConfirmMessage"
                        progress-liner-on-confirm
                    >
                        <template #formItems="{onSave, onCancel}">
                            <slot name="editForm"
                                  v-bind:focusItem="focusItem"
                                  v-bind:formModels="formModels_"
                                  v-bind:onCancel="onCancel"
                                  v-bind:onSave="onSave"
                                  v-bind:outprops="outprops"
                            ></slot>
                        </template>
                    </dynamic-dialog>
                    <slot name="topRight"></slot>
                </v-toolbar>
            </template>
            <template v-for="header in tableHeaders" v-slot:[getSlotName(header.key)]="{ item }">
                <template v-if="header.key=='Actions'">
                    <slot
                        :name="header.key"
                        v-bind:deleteExecute="deleteExecute"
                        v-bind:editExecute="editExecute"
                        v-bind:deleteItem="deleteItem"
                        v-bind:editItem="editItem"
                        v-bind:remove="remove"
                        v-bind:update="update"
                        v-bind:item="<TBaseItem<any>>item">


                        <slot name="actionPrepend" v-bind:item="item"></slot>

                        <k-tool-tip v-if="useEditAction"
                                    color="kpmgPrimary-900"
                                    icon="mdi-pencil"
                                    size="large"
                                    location="top"
                                    :classes="['ml-1']"
                                    :text="$t('generic.crud.action', 0)"
                                    @click="editItem(item)"/>
                        <k-tool-tip v-if="useDeleteAction"
                                    color="trtTealGreen"
                                    icon="mdi-delete"
                                    size="large"
                                    location="top"
                                    :classes="['ml-1']"
                                    :text="$t('generic.crud.action', 1)"
                                    @click="deleteItem(item)"/>

                        <slot name="actionAppend" v-bind:item="item"></slot>
                    </slot>
                </template>
                <template v-else>
                    <slot :name="header.key"
                          v-bind:item="<TBaseItem<any>>item"
                          v-bind:editExecute="editExecute"
                          v-bind:deleteItem="deleteItem"
                          v-bind:editItem="editItem"
                          v-bind:remove="remove"
                          v-bind:update="update">{{
                            parseHeader(item, header.key)
                        }}
                    </slot>
                </template>
            </template>
            <template v-slot:no-data>
                <slot name="no-data">
                </slot>
            </template>
            <template v-slot:loader>
                <slot name="loader">
                    <v-progress-linear :indeterminate="true" color="kpmgPrimary-900"/>
                </slot>
            </template>
        </v-data-table-server>
        <v-data-table
            v-else
            :density="density"
            :headers="(tableHeaders as any)"
            :items="filteredItems"
            :loading="isLoading"
            class="elevation-0 mt-0"
        >
            <template v-slot:top>
                <v-toolbar :flat="true" color="white">
                    <slot name="topLeft"></slot>
                    <v-text-field
                        v-if="searchFilter"
                        v-model="filterTemplate"
                        class="ms-4"
                        prepend-icon="mdi-magnify"
                        variant="underlined"
                        :placeholder="$t('generic.crud.search')"
                    />
                    <v-spacer/>
                    <div/>
                    <v-spacer/>
                    <dynamic-dialog
                        v-model="dialogDelete"
                        :cancel-text="deleteAbortText_"
                        :close-on-confirm="closeCancelDialogOnConfirm"
                        :confirm-text="deleteConfirmText_"
                        :title="deleteTitle_"
                        close-on-cancel
                        confirm-type="delete"
                        max-width="500px"
                        progress-liner-on-confirm
                        @confirm="deleteExecute"
                        :suppress-cancel-message="suppressDeleteCancelMessage"
                        :suppress-confirm-message="suppressDeleteConfirmMessage"
                    >
                        <template #formItems>
                            <slot name="deleteForm"
                                  v-bind:focusItem="focusItem"
                                  v-bind:outprops="outprops"
                            ></slot>
                        </template>
                    </dynamic-dialog>
                    <dynamic-dialog
                        v-model="dialogEdit"
                        :cancel-text="editAbortText_"
                        :close-on-confirm="closeEditDialogOnConfirm"
                        :confirm-disabled="!canSave"
                        :confirm-text="editConfirmText_"
                        :on-confirming="editExecute"
                        :title="editTitle_"
                        activator="#template-activator"
                        close-on-cancel
                        confirm-type="custom"
                        hide-action-btns
                        max-width="1000px"
                        :suppress-cancel-message="suppressEditCancelMessage"
                        :suppress-confirm-message="suppressEditConfirmMessage"
                        progress-liner-on-confirm
                    >
                        <template #formItems="{onSave, onCancel}">
                            <slot name="editForm"
                                  v-bind:focusItem="focusItem"
                                  v-bind:formModels="formModels_"
                                  v-bind:onCancel="onCancel"
                                  v-bind:onSave="onSave"
                                  v-bind:outprops="outprops"
                            ></slot>
                        </template>
                    </dynamic-dialog>
                    <slot name="topRight"></slot>
                </v-toolbar>
            </template>
            <template v-for="header in tableHeaders" v-slot:[getSlotName(header.key)]="{ item }">
                <template v-if="header.key=='Actions'">
                    <slot
                        :name="header.key"
                        v-bind:deleteExecute="deleteExecute"
                        v-bind:editExecute="editExecute"
                        v-bind:deleteItem="deleteItem"
                        v-bind:editItem="editItem"
                        v-bind:remove="remove"
                        v-bind:update="update"
                        v-bind:item="<TBaseItem<any>>item">


                        <slot name="actionPrepend" v-bind:item="item"></slot>

                        <k-tool-tip v-if="useEditAction"
                                    color="kpmgPrimary-900"
                                    icon="mdi-pencil"
                                    size="large"
                                    location="top"
                                    :classes="['ml-1']"
                                    :text="$t('generic.crud.action', 0)"
                                    @click="editItem(item)"/>
                        <k-tool-tip v-if="useDeleteAction"
                                    color="trtTealGreen"
                                    icon="mdi-delete"
                                    size="large"
                                    location="top"
                                    :classes="['ml-1']"
                                    :text="$t('generic.crud.action', 1)"
                                    @click="deleteItem(item)"/>

                        <slot name="actionAppend" v-bind:item="item"></slot>
                    </slot>
                </template>
                <template v-else>
                    <slot :name="header.key"
                          v-bind:item="<TBaseItem<any>>item"
                          v-bind:editExecute="editExecute"
                          v-bind:deleteItem="deleteItem"
                          v-bind:editItem="editItem"
                          v-bind:remove="remove"
                          v-bind:update="update">{{
                            parseHeader(item, header.key)
                        }}
                    </slot>
                </template>
            </template>
            <template v-slot:no-data>
                <slot name="no-data">
                </slot>
            </template>
            <template v-slot:loader>
                <slot name="loader">
                    <v-progress-linear :indeterminate="true" color="kpmgPrimary-900"/>
                </slot>
            </template>
        </v-data-table>
    </div>
</template>


<script lang="ts">
import type {PropType, Ref} from "vue"
import {computed, defineComponent, nextTick, ref, watch} from "vue"
import DynamicDialog from "@/components/generics/DynamicDialog/DynamicDialog.vue";
import {useVModel} from "@vueuse/core";
import type {TDensity} from "./types/TDensity";
import type {TStringCallback} from "./types/TStringCallback";
import type {ITableHeader} from "./interfaces/ITableHeader";
import type {IBaseFormModel} from "./interfaces/IBaseFormModel";
import type {IBeforeCreate} from "./interfaces/IBeforeCreate";
import type {TBaseStore} from "@/stores/BaseStore/types/TBaseStore";
import type {TBaseItem} from "@/stores/BaseStore/types/TBaseItem";
import {IServerSideBaseStore} from "@/stores/BaseStore/interfaces/IBaseStore";
import KToolTip from "@/components/dds/KToolTip/KToolTip.vue";

export default defineComponent({
    emits: ['update:showDialogEdit', 'update:showDialogDelete', 'updated', 'created'],
    components: {
        DynamicDialog,
        KToolTip
    },
    props: {
        tableHeaders: {
            type: Object as PropType<ITableHeader[]>,
            required: true
        },
        searchFilter: {
            type: Function as PropType<((value: TBaseItem<any>, search: string) => boolean)>
        },
        dataFilter: {
            type: Function as PropType<((value: TBaseItem<any>) => boolean)>
        },
        create: {
            type: Boolean,
            default: true
        },
        baseStore: {
            type: Object as PropType<TBaseStore<string>>,
            required: true
        },
        defaultItem: {
            type: Object,
            required: true
        },
        formModels: {
            type: Object as PropType<IBaseFormModel>
        },
        createBtnTitle: {
            type: [Function, String] as PropType<TStringCallback | string>,
            default: 'Erstellen'
        },
        createTitle: {
            type: [Function, String] as PropType<TStringCallback | string>,
            default: 'Erstellen'
        },
        createConfirmText: {
            type: [Function, String] as PropType<TStringCallback | string>,
            default: 'speichern'
        },
        createAbortText: {
            type: [Function, String] as PropType<TStringCallback | string>,
            default: 'abbrechen'
        },
        editTitle: {
            type: [Function, String] as PropType<TStringCallback | string>,
            default: 'Bearbeiten'
        },
        editConfirmText: {
            type: [Function, String] as PropType<TStringCallback | string>,
            default: 'aktualisieren'
        },
        editAbortText: {
            type: [Function, String] as PropType<TStringCallback | string>,
            default: 'abbrechen'
        },
        deleteTitle: {
            type: [Function, String] as PropType<TStringCallback | string>,
            default: 'Löschen'
        },
        deleteConfirmText: {
            type: [Function, String] as PropType<TStringCallback | string>,
            default: 'löschen'
        },
        deleteAbortText: {
            type: [Function, String] as PropType<TStringCallback | string>,
            default: 'abbrechen'
        },
        createKeys: {
            type: Array<string>
        },
        beforeCreate: {
            type: Object as PropType<IBeforeCreate>
        },
        afterCreate: {
            type: Object as PropType<(() => void)[]>
        },
        isForm: {
            type: Boolean,
            default: false
        },
        density: {
            type: String as PropType<TDensity>,
            default: 'compact'
        },
        closeEditDialogOnConfirm: {
            type: Boolean,
            default: true
        },
        closeCancelDialogOnConfirm: {
            type: Boolean,
            default: false
        },
        showDialogEdit: {
            type: Boolean,
            default: false
        },
        showDialogDelete: {
            type: Boolean,
            default: false
        },
        suppressDeleteConfirmMessage: {
            type: Boolean,
            default: false
        },
        suppressDeleteCancelMessage: {
            type: Boolean,
            default: false
        },
        suppressEditConfirmMessage: {
            type: Boolean,
            default: false
        },
        suppressEditCancelMessage: {
            type: Boolean,
            default: false
        },
        serverSideTable: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        useEditAction: {
            type: Boolean,
            default: true
        },
        useDeleteAction: {
            type: Boolean,
            default: true
        },
    },
    setup(props, {emit}) {

        const _loading: Ref<boolean> = ref(false)

        const isLoading = computed(() => {
            if (props.loading) {
                return true
            }
            return _loading.value;
        })
        /**
         * Get a generic typed item
         *
         * @param item
         */
        const getType = <T>(item: T): T => {
            return item
        }

        /**
         * parse the header names to make all slots available as within the v-data table
         * @param item
         * @param header
         */
        const parseHeader = (item, header) => {
            const original = item
            let splitted = header.split('.')
            splitted.forEach((i) => {
                if (item != null && Object.prototype.hasOwnProperty.call(item, i)) {
                    item = item[i]
                }
            })
            return item === original ? '' : item
        }

        /**
         * creates the v-data-table slot name
         *
         * @param test
         */
        const getSlotName = (test: string): string => {
            return 'item.' + test
        }

        /**
         * the underlying base store to communicate with
         */
        const baseStore = props.baseStore

        /**
         * container to keep every model in the context
         */
        const formModels_: Ref<IBaseFormModel> = ref({});

        /**
         * parse and make them reactive
         */
        if (props.formModels) {
            for (const [key] of Object.entries(formModels_.value)) {
                formModels_.value[key] = useVModel(formModels_.value, key, emit)
            }
        }


        /**
         * a optional search filter
         */
        const filterFunc: Function | undefined = props.searchFilter

        /**
         * a optional data filter intended to us to prefilter data
         */
        const dataFilterFunc: Function | undefined = props.dataFilter

        /**
         * reactive toggle to show the delete and edit dialogs
         */
        const dialogDelete = useVModel(props, 'showDialogDelete', emit, {passive: true})
        const dialogEdit = useVModel(props, 'showDialogEdit', emit, {passive: true})

        /**
         * indicator if saving is currently happening
         */

        const formValidation = ref<any>(null)

        /**
         * computed properties choosing between callback function or string
         */
        const createBtnTitle_ = computed<string>(() => {
            if (typeof props.createBtnTitle === 'function') {
                return props.createBtnTitle(focusItem.value)
            }
            return props.createBtnTitle
        })

        const editTitle_ = computed<string>(() => {
            if (focusItem.value.id) {
                if (typeof props.editTitle === 'function') {
                    return props.editTitle(focusItem.value)
                }
                return props.editTitle
            } else {
                if (typeof props.createTitle === 'function') {
                    return props.createTitle(focusItem.value)
                }
                return props.createTitle
            }
        })

        const editConfirmText_ = computed<string>(() => {
            if (focusItem.value.id) {
                if (typeof props.editConfirmText === 'function') {
                    return props.editConfirmText(focusItem.value)
                }
                return props.editConfirmText
            } else {
                if (typeof props.createConfirmText === 'function') {
                    return props.createConfirmText(focusItem.value)
                }
                return props.createConfirmText
            }
        })

        const editAbortText_ = computed<string>(() => {
            if (focusItem.value.id) {
                if (typeof props.editAbortText === 'function') {
                    return props.editAbortText(focusItem.value)
                }
                return props.editAbortText
            } else {
                if (typeof props.createAbortText === 'function') {
                    return props.createAbortText(focusItem.value)
                }
                return props.createAbortText
            }
        })

        const deleteTitle_ = computed<string>(() => {
            if (typeof props.deleteTitle === 'function') {
                return props.deleteTitle(focusItem.value)
            }
            return props.deleteTitle
        })

        const deleteConfirmText_ = computed<string>(() => {
            if (typeof props.deleteConfirmText === 'function') {
                return props.deleteConfirmText(focusItem.value)
            }
            return props.deleteConfirmText
        })

        const deleteAbortText_ = computed<string>(() => {
            if (typeof props.deleteAbortText === 'function') {
                return props.deleteAbortText(focusItem.value)
            }
            return props.deleteAbortText
        })

        const focusItem: Ref<TBaseItem<any>> = ref({...props.defaultItem})

        const filterTemplate = ref<string>("");

        /**
         * data items after filter processing
         */
        const filteredItems = computed((): any[] => {
            let result = props.baseStore.items
            if (dataFilterFunc && result != undefined) {
                result = result.filter((item) => dataFilterFunc(item))
            }
            if (filterFunc && result != undefined) {
                result = result.filter((item) => filterFunc(item, filterTemplate.value))
            }
            return result
        })

        /**
         * indicators
         */
        const canSave = computed((): boolean => {
            return formValidation.value !== false
        })
        const loading = computed((): boolean => {
            return !baseStore.initialized;
        })
        const saving = ref<boolean>(false)

        const validDate = computed({
            // getter
            get() {
                if (typeof focusItem.value.validUntil === "string") {
                    return new Date(focusItem.value.validUntil)
                }
                return null

            },
            // setter
            set(newValue) {
                if (newValue) {
                    focusItem.value.validUntil = newValue.toISOString()
                } else {
                    focusItem.value.validUntil = null
                }

            }
        })

        const outprops = ref({
            'validDate': validDate
        })
        // actions for delete form
        /**
         * aborts the cancel modal and reset the focus item
         */
        const deleteCancel = (): void => {
            dialogDelete.value = false
            focusItem.value = {...props.defaultItem}
        }
        /**
         * prepares the focus item and opens the delete modal
         * @param item
         */
        const deleteItem = (item: TBaseItem<any>): void => {
            focusItem.value = {...item}
            dialogDelete.value = true
        }
        /**
         * executes the actual delete
         */
        const deleteExecute = () => {
            if (focusItem.value.id && baseStore.remove) {
                baseStore.remove(focusItem.value.id)
            }
            dialogDelete.value = false
            focusItem.value = {...props.defaultItem}
        }
        /**
         * performs delete without having the modal involved
         * @param item
         */
        const remove = (item: TBaseItem<object>): void => {
            focusItem.value = {...item}
            editExecute();
        }

        // actions for edit/cancel form
        /**
         * aborts the edit modal and reset the focus item
         */
        const editCancel = (): void => {
            dialogEdit.value = false
            focusItem.value = {...props.defaultItem}
        }
        /**
         * prepares the focus item and opens the edit modal
         * @param item
         */
        const editItem = (item: TBaseItem<object>): void => {
            focusItem.value = {...item}
            dialogEdit.value = true
        }
        /**
         * executes the actual update or create action
         */
        const editExecute = async (): Promise<void> => {
            let result: TBaseItem<Object | FormData> = _parseItem()
            if (focusItem.value.id && baseStore.update) {
                await baseStore.update(focusItem.value.id, result)
                if (props.afterCreate) {
                    props.afterCreate.forEach((item) => {
                        item()
                    })
                }
                emit('updated');

            } else {
                if (props.isForm) {
                    let formData = new FormData();
                    for (const [key, value] of Object.entries(result)) {
                        if (value instanceof File || typeof value === "string") {
                            formData.append(key, value)
                        }
                    }
                    result = <TBaseItem<Object>>{form: formData, item: focusItem.value};

                }
                if (baseStore.create) {
                    await baseStore.create(result);
                    if (props.afterCreate) {
                        props.afterCreate.forEach((item) => {
                            item();
                        })
                    }
                    emit('created');
                }

            }
            dialogEdit.value = false;
            focusItem.value = {...props.defaultItem};


        }
        /**
         * performs create or update without having the modal involved
         * @param item
         */
        const update = (item: TBaseItem<object>): void => {
            focusItem.value = {...item}
            editExecute();
        }
        const _getValue = (key: string, value: any) => {
            if (props.createKeys?.includes(key) || !props.createKeys) {
                if (props.beforeCreate?.hasOwnProperty(key)) {
                    return props.beforeCreate[key](value);
                } else {
                    return value;
                }
            }
        }
        const _parseItem = (): TBaseItem<Object | FormData> => {
            let result: TBaseItem<Object | FormData> = {};
            for (const [key, value] of Object.entries(focusItem.value)) {
                let v = _getValue(key, value)
                if (v !== undefined) {
                    result[key] = v
                }
            }
            for (const [key, value] of Object.entries(formModels_.value)) {
                let v = _getValue(key, value)
                if (v !== undefined) {
                    result[key] = v
                }
            }
            return result
        }
        const loadItems = async (items: IServerSideBaseStore) => {
            if (props.serverSideTable && props.baseStore.reload != undefined) {
                _loading.value = true;
                await props.baseStore.reload(items)
                _loading.value = false;
            }
        }

        const totalItems = computed(() => {
            if (props.baseStore.totalItemCount) {
                return props.baseStore.totalItemCount;
            }
            if (props.baseStore.items) {
                return props.baseStore.items.length;
            }
            return 0

        })

        watch(dialogEdit, (nV) => {
            if (!nV) {
                nextTick(() => focusItem.value = {...props.defaultItem})
            }
        })
        return {
            filterTemplate,
            filteredItems,
            dialogDelete,
            dialogEdit,
            formValidation,
            focusItem,
            saving,
            canSave,
            loading,
            validDate,
            outprops,
            formModels_,
            createBtnTitle_,
            editTitle_,
            editConfirmText_,
            editAbortText_,
            deleteTitle_,
            deleteConfirmText_,
            deleteAbortText_,
            getType,
            editItem,
            editExecute,
            editCancel,
            deleteCancel,
            deleteItem,
            deleteExecute,
            getSlotName,
            parseHeader,
            update,
            remove,
            loadItems,
            totalItems,
            isLoading

        }
    },
})
</script>

<!--<style lang="scss" scoped></style>-->
